import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaChevronRight, FaFutbol, FaBasketballBall, FaVolleyballBall, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';
import { GiTennisRacket } from 'react-icons/gi';
import '../../styles/pages/sportsAppointment.scss';
import { getSportsRegionsWithFacilities, getSportsAvailableSlots, createSportsAppointment, verifySportsAppointment } from '../../api/sportsAppointmentService';

const facilityIcons = {
  Tenis: GiTennisRacket,
  Futbol: FaFutbol,
  Basketbol: FaBasketballBall,
  Voleybol: FaVolleyballBall,
  'Basketbol/Voleybol': FaBasketballBall,
};

const facilityCoordinates = {
  'Rauf Denktas Tenis Kortlari': '36.587956, 36.146827',
  'Toki Millet Parki Tesisleri': '36.587563, 36.163551',
  'Millet Bahçesi Spor Tesisleri': '36.585065, 36.155411',
  'Mustafa Kemal Kapali Spor Salonu': '36.564500, 36.152073',
  'Denizciler Kapalı Spor Salonu': '36.564500, 36.152073'
};

const SportsAppointment = () => {
  const [regions, setRegions] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    return today.toISOString().split('T')[0];
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  const handleTimeSlotSelected = (slot) => {
    setSelectedTimeSlot(slot);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Verification modal states
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Success modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Bölgeleri ve tesisleri API'den çek
  useEffect(() => {
    fetchRegionsAndFacilities();
  }, []);

  // Tarih değiştiğinde saat dilimlerini yenile
  useEffect(() => {
    if (selectedFacility) {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
    }
  }, [selectedDate, selectedFacility?.id]);

  const fetchRegionsAndFacilities = async () => {
    try {
      setLoading(true);
      const response = await getSportsRegionsWithFacilities();
      if (response.isSuccess) {
        setRegions(response.data || []);
        setError(null);
      } else {
        setError(response.resultMessage || 'Veriler yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Bölgeler yüklenirken hata:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTimeSlots = async (facilityId, date) => {
    try {
      const response = await getSportsAvailableSlots(facilityId, date);

      if (response.isSuccess) {
        setSelectedFacility(prev => ({
          ...prev,
          timeSlots: response.data || []
        }));
      }
    } catch (err) {
      console.error('Saat dilimleri yüklenirken hata:', err);
    }
  };

  const handleCreateAppointment = async () => {
    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone) {
      alert('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }

    try {
      const appointmentData = {
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.idNumber,
        email: formData.email,
        phone: formData.phone,
        createdByWeb: true, // Website'den oluşturuluyor
      };

      const response = await createSportsAppointment(appointmentData);

      if (response.isSuccess) {
        // Email gönderildiyse verification modal göster
        if (response.data?.verificationEmailSent) {
          setPendingAppointmentId(response.data.appointmentId);
          setShowVerificationModal(true);
          setVerificationCode('');
          setVerificationError('');
        } else {
          // Direkt onaylandı (olmaması gereken durum)
          alert('Randevunuz oluşturulmuştur.');

          // Form temizle ve saat dilimlerini yenile
          setFormData({ firstName: '', lastName: '', idNumber: '', email: '', phone: '' });
          setSelectedTimeSlot(null);
          fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
        }
      } else {
        alert(response.resultMessage || 'Randevu oluşturulurken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Randevu oluşturulurken hata:', err);
      alert('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Lütfen 6 haneli doğrulama kodunu giriniz.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const response = await verifySportsAppointment(pendingAppointmentId, verificationCode);

      if (response.isSuccess) {
        setShowVerificationModal(false);
        setSuccessMessage({
          firstName: formData.firstName,
          lastName: formData.lastName,
          facilityName: selectedFacility.name,
          date: new Date(selectedDate).toLocaleDateString('tr-TR'),
          time: selectedTimeSlot.time
        });
        setShowSuccessModal(true);

        // Form temizle ve saat dilimlerini yenile
        setFormData({ firstName: '', lastName: '', idNumber: '', email: '', phone: '' });
        setSelectedTimeSlot(null);
        setVerificationCode('');
        setPendingAppointmentId(null);
        fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
      } else {
        setVerificationError(response.resultMessage || 'Doğrulama kodu hatalı.');
      }
    } catch (err) {
      console.error('Doğrulama hatası:', err);
      setVerificationError('Doğrulama kodu hatalı veya süresi dolmuş.');
    } finally {
      setIsVerifying(false);
    }
  };

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(id => id !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };

  const handleFacilityClick = async (facility, region) => {
    setSelectedFacility({
      ...facility,
      regionName: region.name,
      timeSlots: []
    });
    setSelectedTimeSlot(null);

    // Saat dilimlerini çek
    await fetchAvailableTimeSlots(facility.id, selectedDate);
  };

  if (loading) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-loading">
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-error">
            <p>{error}</p>
            <button onClick={fetchRegionsAndFacilities} className="sa-retry-button">Tekrar Dene</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-appointment-page">
      <div className="container">
        <header className="sa-header">
          <h1>Spor Tesisleri Randevu Sistemi</h1>
          <p>
            İskenderun Belediyesi spor tesislerini online randevu sistemi ile kiralayabilirsiniz.
            Tenis kortları, futbol sahaları, basketbol ve voleybol sahaları saatlik kiralama ile hizmetinizde.
          </p>
        </header>

        <div className="sa-content">
          {/* Sol Panel - Bölgeler ve Sahalar */}
          <div className="sa-left-panel">
            {/* Bölgeler Listesi */}
            <div className="sa-regions-list">
              {regions.map(region => (
                <div key={region.id} className="sa-region-item">
                  <button
                    type="button"
                    className={`sa-region-header ${expandedRegions.includes(region.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRegion(region.id)}
                  >
                    <span className="sa-region-icon">
                      {expandedRegions.includes(region.id) ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                    <span className="sa-region-name">{region.name}</span>

                  </button>

                  {expandedRegions.includes(region.id) && (
                    <div className="sa-facilities-list">
                      {region.facilities.map(facility => {
                        const Icon = facilityIcons[facility.type] || FaFutbol;
                        const isSelected = selectedFacility?.id === facility.id;

                        return (
                          <button
                            key={facility.id}
                            type="button"
                            className={`sa-facility-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleFacilityClick(facility, region)}
                          >
                            <div className="sa-facility-icon">
                              <Icon />
                            </div>
                            <div className="sa-facility-info">
                              <div className="sa-facility-name">{facility.name}</div>

                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sağ Panel - Randevu Detayları */}
          <div className="sa-right-panel">
            {selectedFacility ? (
              <div className="sa-facility-details">
                <div className="sa-facility-details-header">
                  <div className="sa-facility-title">
                    <h3>{selectedFacility.name}</h3>
                    <span // turbo-all
                      className="sa-facility-region clickable"
                      onClick={() => {
                        const coords = facilityCoordinates[selectedFacility.regionName];
                        if (coords) {
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords}`, '_blank');
                        }
                      }}
                      title={facilityCoordinates[selectedFacility.regionName] ? "Haritada görmek için tıklayın" : ""}
                    >
                      <FaMapMarkerAlt /> {selectedFacility.regionName}
                    </span>
                  </div>
                </div>

                <div className="sa-facility-specs">
                  <div className="sa-spec-item">
                    <span className="sa-spec-label">Tür</span>
                    <span className="sa-spec-value">{selectedFacility.type}</span>
                  </div>
                  <div className="sa-spec-item">
                    <span className="sa-spec-label">Kapasite</span>
                    <span className="sa-spec-value">{selectedFacility.capacity}</span>
                  </div>
                  <div className="sa-spec-item">
                    <span className="sa-spec-label">Kiralama Süresi</span>
                    <span className="sa-spec-value">1 Saat</span>
                  </div>
                </div>

                <div className="sa-appointment-section">
                  <div className="sa-date-picker-section">
                    <label htmlFor="appointment-date">Tarih Seçin</label>
                    <input
                      id="appointment-date"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="sa-date-input"
                    />
                  </div>

                  <div className="sa-time-slots-section">
                    <h4>Müsait Saatler <span className="sa-working-hours">(08:00 - 23:59)</span></h4>
                    <div className="sa-time-slots-grid">
                      {selectedFacility.timeSlots.map((slot, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`sa-time-slot ${slot.available ? 'available' : 'occupied'} ${selectedTimeSlot?.time === slot.time ? 'selected' : ''}`}
                          disabled={!slot.available}
                          onClick={() => slot.available && handleTimeSlotSelected(slot)}
                        >
                          <span className="sa-slot-time">{slot.time}</span>
                          <span className="sa-slot-status">
                            {slot.available ? 'Müsait' : 'Dolu'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedTimeSlot && (
                    <div className="sa-booking-form" ref={formRef}>
                      <h4>Randevu Bilgileri</h4>
                      <div className="sa-form-grid">
                        <div className="sa-form-group">
                          <label htmlFor="firstName">Ad <span className="sa-required">*</span></label>
                          <input
                            id="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            placeholder="Adınızı giriniz"
                            className="sa-form-input"
                          />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="lastName">Soyad <span className="sa-required">*</span></label>
                          <input
                            id="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            placeholder="Soyadınızı giriniz"
                            className="sa-form-input"
                          />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="idNumber">T.C. Kimlik No <span className="sa-required">*</span></label>
                          <input
                            id="idNumber"
                            type="text"
                            value={formData.idNumber}
                            onChange={(e) => setFormData({ ...formData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                            placeholder="TC Kimlik No giriniz"
                            className="sa-form-input"
                            maxLength="11"
                          />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="email">E-Posta <span className="sa-required">*</span></label>
                          <input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="E-posta adresiniz"
                            className="sa-form-input"
                          />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="phone">Telefon <span className="sa-required">*</span></label>
                          <input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="5XXXXXXXXX"
                            className="sa-form-input"
                          />
                        </div>

                        <div className="sa-form-group sa-appointment-summary">
                          <label>Randevu Saati</label>
                          <div className="sa-summary-box">
                            <strong>{selectedTimeSlot.time}</strong>
                            <span>{new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="sa-submit-button"
                        onClick={handleCreateAppointment}
                      >
                        Randevu Oluştur
                      </button>
                    </div>
                  )}


                </div>
              </div>
            ) : (
              <div className="sa-no-selection">
                <FaFutbol className="sa-no-selection-icon" />
                <h3>Saha Seçin</h3>
                <p>Randevu almak için sol panelden bölge ve saha seçiniz</p>
              </div>
            )}
          </div>
        </div>

        {/* Verification Modal */}
        {showVerificationModal && (
          <div className="sa-verification-modal-overlay" onClick={() => !isVerifying && setShowVerificationModal(false)}>
            <div className="sa-verification-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3>Email Doğrulama</h3>
                <button
                  className="sa-modal-close"
                  onClick={() => setShowVerificationModal(false)}
                  disabled={isVerifying}
                >
                  ×
                </button>
              </div>
              <div className="sa-modal-body">
                <p className="sa-verification-instruction">
                  E-posta adresinize gönderilen 6 haneli doğrulama kodunu giriniz:
                </p>
                <input
                  type="text"
                  className="sa-verification-input"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setVerificationCode(value);
                    if (verificationError) setVerificationError('');
                  }}
                  placeholder="000000"
                  disabled={isVerifying}
                  autoFocus
                />
                {verificationError && (
                  <div className="sa-verification-error">{verificationError}</div>
                )}
                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={handleVerifyCode}
                    disabled={isVerifying || verificationCode.length !== 6}
                  >
                    {isVerifying ? 'Doğrulanıyor...' : 'Onayla'}
                  </button>
                  <button
                    className="sa-btn-cancel"
                    onClick={() => setShowVerificationModal(false)}
                    disabled={isVerifying}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && successMessage && (
          <div className="sa-verification-modal-overlay" onClick={() => setShowSuccessModal(false)}>
            <div className="sa-verification-modal sa-success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-body">
                <div className="sa-success-icon">
                  <FaCheckCircle />
                </div>
                <h3>Randevunuz Onaylandı!</h3>
                <p>Bilgileriniz mail üzerinden gönderilmiştir.</p>

                <div className="sa-success-details">
                  <div className="sa-detail-row">
                    <span className="label">Ad Soyad:</span>
                    <span className="value">{successMessage.firstName} {successMessage.lastName}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Saha:</span>
                    <span className="value">{successMessage.facilityName}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Tarih:</span>
                    <span className="value">{successMessage.date}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Saat:</span>
                    <span className="value">{successMessage.time}</span>
                  </div>
                </div>

                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={() => setShowSuccessModal(false)}
                  >
                    Tamam
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportsAppointment;
